import { KeyboardEvent } from 'react';
import { Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChainAvatar } from '@Components/ConnectedChainCard';
import { Schedule } from '@Models';
import { getRetailerColor, getRetailerName, getRetailerShort } from '../../FieldServices/constants';
import { ScheduleEnrichment } from '../enrichment';
import { formatScheduleRecurrence } from '../Wizard/constants';
import FirmAvatarStack from './firmAvatarStack';

interface ScheduleCardProps {
  schedule: Schedule;
  enrichment: ScheduleEnrichment;
  colorsById: Record<string, string>;
}

const ScheduleCard = ({ schedule, enrichment, colorsById }: ScheduleCardProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const primaryRetailer = enrichment.retailers[0];
  const firms = enrichment.firmIds.map((id, index) => ({ id, name: enrichment.firmNames[index] }));
  const openDetail = () => navigate(`/schedules/${schedule.id}`);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDetail();
        }
      }}
      className="relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-colors hover:border-brand-400 hover:shadow-md">
      {schedule.status === 'paused' ? (
        <span
          title={t('Schedule.Status.paused')}
          className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-gray-500">
          <Pause size={11} />
          <span className="sr-only">{t('Schedule.Status.paused')}</span>
        </span>
      ) : schedule.status === 'active' ? (
        <span title={t('Schedule.Status.active')} className="absolute right-3 top-3 flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success-500" />
          <span className="sr-only">{t('Schedule.Status.active')}</span>
        </span>
      ) : (
        <span
          title={t('Schedule.Status.ended')}
          className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-gray-300">
          <span className="sr-only">{t('Schedule.Status.ended')}</span>
        </span>
      )}
      <div className="flex items-start gap-3 px-4 pb-3 pt-4">
        {primaryRetailer && (
          <ChainAvatar
            size="sm"
            initials={getRetailerShort(primaryRetailer)}
            color={getRetailerColor(primaryRetailer)}
            textColor="#FFFFFF"
            alt={getRetailerName(primaryRetailer)}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-sm font-bold leading-snug text-gray-900">{schedule.name}</div>
          {enrichment.retailers.length > 0 && (
            <div className="mt-1 truncate text-xs text-gray-500">
              {enrichment.retailers.map(getRetailerName).join(', ')}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2">
        <div className="px-4 py-2.5">
          <div className="text-[9.5px] font-bold uppercase tracking-wide text-gray-500">
            {t('Schedule.Card.Stores')}
          </div>
          <div className="mt-0.5 text-sm font-bold tabular-nums text-gray-900">{enrichment.storeCount}</div>
        </div>
        <div className="px-4 py-2.5">
          <div className="text-[9.5px] font-bold uppercase tracking-wide text-gray-500">
            {t('Schedule.List.Cadence')}
          </div>
          <div className="mt-1 text-xs font-semibold text-gray-900">
            {formatScheduleRecurrence(schedule, t, i18n.language)}
          </div>
        </div>
      </div>

      <div className="px-4 py-2.5">
        <FirmAvatarStack firms={firms} colorsById={colorsById} />
      </div>
    </div>
  );
};

export default ScheduleCard;
